import { useEffect, useMemo, useRef, useState, type RefObject } from "react"
import type { AgGridReact } from "ag-grid-react"
import type { GridOptions } from "ag-grid-community"

import { useFilters } from "~/hooks/useFilters"
import type { SettingField } from "~/utils/allocationLineSettings"
import type { FilterFieldType } from "~/utils/filter"
import { filterRowsById } from "../../AllocationFilters"
import { distinctValues } from "~/utils/tsUtils"
import { matchesLineSearch, VARIANT_SEARCH_OPTIONS } from "~/utils/lineSearch"
import { useLineSearch } from "../../useLineSearch"
import type { RangePackage } from "../../types"
import { variantCounts, type VariantSummary } from "./productSettingsModel"
import { SETTING_FIELD_META } from "./productSettingFields"
import type { Row } from "./productRows"
import type { ProductListing } from "./useProductSettings"

export type OverrideView = "all" | "with" | "without"

/** What the filter popover filters on: one entry per variant. */
type VariantFilterRow = {
  id: string
  productId: string
  productName: string
  variantId: string
  variantName: string
  category: string
  color: string | null
  packages: string[]
  sizes: string[]
  skuIds: string[]
  settings: "Has overrides" | "No overrides"
} & Record<SettingField, number>

const FILTER_FIELDS: FilterFieldType[] = [
  { value: "productName", name: "Product name", type: "string" },
  { value: "variantName", name: "Variant name", type: "string" },
  { value: "category", name: "Class", type: "string" },
  { value: "packages", name: "Product package", type: "string" },
  { value: "sizes", name: "Size", type: "string" },
  { value: "settings", name: "Settings", type: "string" },
  { value: "minDisplayQty", name: SETTING_FIELD_META.minDisplayQty.filterName, type: "number" },
  { value: "maxQty", name: SETTING_FIELD_META.maxQty.filterName, type: "number" },
  { value: "excluded", name: SETTING_FIELD_META.excluded.filterName, type: "number" },
  { value: "orderMultiple", name: SETTING_FIELD_META.orderMultiple.filterName, type: "number" },
]
const filterFieldType = (field: string) =>
  FILTER_FIELDS.find((candidate) => candidate.value === field)?.type

/** The previous set while `next` holds the same ids, so a draft edit that moves no variant in or out refilters nothing. */
function useStableSet(next: ReadonlySet<string>): ReadonlySet<string> {
  const [kept, setKept] = useState(next)
  if (kept === next) return kept
  if (kept.size === next.size && [...next].every((id) => kept.has(id))) return kept
  setKept(next)
  return next
}

/** The search, the filter popover and the overrides view, and the variants that pass them all. */
export function useProductFilters({
  tableId,
  listings,
  summaries,
  packages,
  packageNamesOf,
}: {
  tableId: string
  listings: ReadonlyMap<string, ProductListing>
  summaries: ReadonlyMap<string, VariantSummary>
  packages: Pick<RangePackage, "name">[]
  packageNamesOf: ReadonlyMap<string, string[]>
}) {
  const { search, setSearch, searchBy, setSearchBy } = useLineSearch(
    tableId,
    VARIANT_SEARCH_OPTIONS,
  )
  const { filters, setFilters } = useFilters()
  const [overrideView, setOverrideView] = useState<OverrideView>("all")

  const filterRows = useMemo(
    () =>
      [...listings.values()].flatMap((listing): VariantFilterRow[] => {
        const summary = summaries.get(listing.scopeId)
        if (!summary) return []
        const counts = variantCounts(summary)
        return [
          {
            id: listing.variantId,
            productId: listing.productId,
            productName: listing.productName,
            variantId: listing.variantId,
            variantName: listing.variantName,
            category: listing.category ?? "",
            color: listing.color,
            packages: packageNamesOf.get(listing.variantId) ?? [],
            sizes: listing.skus.map((sku) => sku.size),
            skuIds: listing.skus.map((sku) => sku.skuId),
            settings: summary.hasOverrides ? "Has overrides" : "No overrides",
            excluded: counts.excluded,
            minDisplayQty: counts.minDisplayQty,
            maxQty: counts.maxQty,
            orderMultiple: counts.orderMultiple,
          },
        ]
      }),
    [listings, summaries, packageNamesOf],
  )
  const filterFieldsOverride = useMemo(
    () => ({
      fields: FILTER_FIELDS,
      optionsByField: {
        productName: distinctValues(filterRows.map((row) => row.productName)),
        variantName: distinctValues(filterRows.map((row) => row.variantName)),
        category: distinctValues(filterRows.map((row) => row.category)),
        packages: distinctValues(packages.map((pkg) => pkg.name)),
        sizes: distinctValues(filterRows.flatMap((row) => row.sizes)),
        settings: ["Has overrides", "No overrides"],
      },
    }),
    [filterRows, packages],
  )

  // The grid filters on this itself, so a filter change never hands it a new set of ~100k rows.
  const matching = useMemo(() => {
    const searched = filterRows.filter((row) => {
      if (!matchesLineSearch(row, search, searchBy)) return false
      if (overrideView === "with") return row.settings === "Has overrides"
      if (overrideView === "without") return row.settings === "No overrides"
      return true
    })
    return new Set(filterRowsById(searched, filters, filterFieldType).map((row) => row.id))
  }, [filterRows, search, searchBy, filters, overrideView])
  const visibleVariants = useStableSet(matching)

  const viewCounts = useMemo(() => {
    const withOverrides = filterRows.filter((row) => row.settings === "Has overrides").length
    return {
      all: filterRows.length,
      with: withOverrides,
      without: filterRows.length - withOverrides,
    } satisfies Record<OverrideView, number>
  }, [filterRows])

  return {
    search,
    setSearch,
    searchBy,
    setSearchBy,
    filters,
    setFilters,
    overrideView,
    setOverrideView,
    viewCounts,
    filterFieldsOverride,
    visibleVariants,
    filtering: search.trim() !== "" || filters.length > 0 || overrideView !== "all",
  }
}

/**
 * The grid's external filter: only variant rows decide, and a group shows when one of its variants
 * does. Read through a ref, since the grid calls it outside render.
 */
export function useVariantExternalFilter(
  gridRef: RefObject<AgGridReact<Row> | null>,
  filtering: boolean,
  visibleVariants: ReadonlySet<string>,
): Pick<GridOptions<Row>, "isExternalFilterPresent" | "doesExternalFilterPass"> {
  const filter = useRef({ filtering, visibleVariants })
  useEffect(() => {
    filter.current = { filtering, visibleVariants }
    gridRef.current?.api?.onFilterChanged()
  }, [gridRef, filtering, visibleVariants])
  return useMemo(
    () => ({
      isExternalFilterPresent: () => filter.current.filtering,
      // A store's placeholder only exists while that store has visible variants.
      doesExternalFilterPass: ({ data }) =>
        data === undefined ||
        data.kind === "placeholder" ||
        (data.kind === "variant" && filter.current.visibleVariants.has(data.listing.variantId)),
    }),
    [],
  )
}
